import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import MonthYearSelector from '../components/MonthYearSelector'
import MonthlyMetricsForm from '../components/MonthlyMetricsForm'
import UserEmailBadge from '../components/UserEmailBadge'

const CustomerDataEntryPage = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['dashboard', 'common'])

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) {
      console.error('Error signing out:', error)
    }
    navigate('/login')
  }
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to current month
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [currentData, setCurrentData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchDataForMonth()
  }, [selectedDate, user])

  const fetchDataForMonth = async () => {
    if (!user || !selectedDate) return

    try {
      setLoading(true)
      
      // Get user's business
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (businessError) {
        console.log('No business found for user')
        return
      }

      // Fetch metrics for selected month
      const { data: metrics, error: metricsError } = await supabase
        .from('monthly_metrics')
        .select('*')
        .eq('business_id', business.id)
        .eq('month', selectedDate.getMonth() + 1)
        .eq('year', selectedDate.getFullYear())
        .single()

      if (metricsError) {
        // No data for this month yet
        setCurrentData(null)
      } else {
        setCurrentData(metrics)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setCurrentData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate)
  }

  const handleDataSave = (savedData) => {
    setCurrentData(savedData)
  }

  const handleFirstMetricSaved = () => {
    navigate('/dashboard')
  }

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      month: 'long', 
      year: 'numeric' 
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5 sm:py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              <span>Interactive </span>
              <span className="text-[#FF1B54]">Business</span>
            </h1>

            <div className="flex items-center space-x-4">
              <UserEmailBadge email={user?.email} showProfileMenu />
              <button
                onClick={handleLogout}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.logout', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-5 sm:px-0 sm:py-7">
          <div className="mb-7">
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              {t('customer.subtitle')}
            </div>
            <p className="mt-2 text-base leading-6 text-gray-600">
              {t('customer.monthlyDataEntrySubtitle')}
            </p>
          </div>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex items-end gap-5 sm:gap-7">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.dashboard', { ns: 'common' })}
              </button>
              <span className="py-2.5 px-1 border-b-2 border-[#FF1B54] font-semibold text-sm text-[#FF1B54]">
                {t('customer.monthlyMetrics')}
              </span>
            </nav>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(340px,0.9fr)_minmax(0,2.1fr)] gap-5 xl:gap-6 items-start">
              {/* Month Selector - Left Sidebar */}
              <div>
                <MonthYearSelector
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                />
                
                {/* Current Data Summary */}
                {currentData && (
                  <div className="mt-5 bg-white border border-gray-200 shadow-sm rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      {t('customer.currentDataFor', { period: formatDateDisplay(selectedDate) })}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.revenue')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.revenue?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.expenses')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.expenses?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.leads')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.leads?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.customers')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.customers?.toLocaleString() || '0'}
                        </span>
                      </div>
                      {/* Social Following (temporarily removed) */}
                      {/**
                       * TODO: Re-enable when we decide how/where it should be used.
                       */}
                      {/*
                      <div className="flex justify-between">
                        <span className="text-gray-600">Social Following:</span>
                        <span className="font-medium">
                          {currentData.social_following?.toLocaleString() || '0'}
                        </span>
                      </div>
                      */}
                    </div>
                  </div>
                )}
              </div>

              {/* Metrics Form - Right Main Area */}
              <div className="min-w-0">
                {loading ? (
                  <div className="bg-white border border-gray-200 border-t-[3px] border-t-[#FF1B54] shadow-sm rounded-2xl p-6 sm:p-8">
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 border-b-[#FF1B54]"></div>
                    </div>
                  </div>
                ) : (
                  <MonthlyMetricsForm
                    selectedDate={selectedDate}
                    existingData={currentData}
                    onSave={handleDataSave}
                    onDataChange={fetchDataForMonth}
                    onFirstMetricSaved={handleFirstMetricSaved}
                  />
                )}
              </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default CustomerDataEntryPage
